import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  DialogActions,
  DialogContent,
  TextField
} from "@mui/material";
import { useTranslation } from "react-i18next";
import DialogComponent from "../../Dialog/Dialog";
import ActionButton from "../../Button/ActionButton";
import HelpButton from "../HelpContent/HelpButton";
import useUpdatePaymentConfiguration from "../../../hooks/useUpdatePaymentConfiguration";
import { PaymentConfiguration } from "../../../types/payments";
import {
  AT_CREDENTIAL_MIN_LENGTH,
  EASYPAY_ACCOUNT_ID_LENGTH,
  PAYMENTS_HELP_CONTENT
} from "../../../constants";
import { outlinedFieldSx } from "../../../utils/fieldStyles";

/**
 * Stores the Portal das Finanças credentials used to issue invoices.
 *
 * Once Easypay has created the account the fields start locked: changing the
 * credentials at that point can break invoice processing, so unlocking them is
 * an explicit, confirmed action.
 */
export default function AtCredentialsDialog({
  open,
  configuration,
  onClose,
  onSaved
}: {
  open: boolean;
  configuration: PaymentConfiguration;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const {
    updateTaxAuthorityCredentials,
    loading
  } = useUpdatePaymentConfiguration();

  const hasEasypayAccount =
    configuration.easypayAccountId?.length === EASYPAY_ACCOUNT_ID_LENGTH;

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [unlocked, setUnlocked] = useState(!hasEasypayAccount);
  const [confirmingUnlock, setConfirmingUnlock] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset whenever the dialog is reopened, so a previous attempt does not leak
  // into the next one.
  useEffect(() => {
    if (!open) return;
    setUsername(configuration.AT?.username ?? "");
    setPassword("");
    setUnlocked(!hasEasypayAccount);
    setConfirmingUnlock(false);
    setError(null);
  }, [open, configuration.AT?.username, hasEasypayAccount]);

  // Only complain once something has been typed, not on an untouched field.
  const usernameTooShort =
    username.length > 0 && username.trim().length < AT_CREDENTIAL_MIN_LENGTH;
  const passwordTooShort =
    password.length > 0 && password.trim().length < AT_CREDENTIAL_MIN_LENGTH;
  const isValid =
    username.trim().length >= AT_CREDENTIAL_MIN_LENGTH &&
    password.trim().length >= AT_CREDENTIAL_MIN_LENGTH;

  const onSubmit = useCallback(async () => {
    setError(null);
    try {
      await updateTaxAuthorityCredentials(configuration.id, username, password);
      onSaved();
      onClose();
    } catch {
      setError(t("paymentAtCredentials.submitError"));
    }
  }, [
    updateTaxAuthorityCredentials,
    configuration.id,
    username,
    password,
    onSaved,
    onClose,
    t
  ]);

  return (
    <>
      <DialogComponent
        open={open}
        handleModal={onClose}
        headerTxt={t("paymentAtCredentials.title")}
      >
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <HelpButton linkName={PAYMENTS_HELP_CONTENT.AT_UPDATE} />
            </Box>
            <TextField
              label={t("paymentAtCredentials.username")}
              placeholder={t("paymentAtCredentials.username")}
              value={username}
              onChange={event => setUsername(event.target.value)}
              disabled={!unlocked}
              error={usernameTooShort}
              helperText={
                usernameTooShort ? t("paymentAtCredentials.tooShort") : ""
              }
              fullWidth
              sx={outlinedFieldSx}
            />
            <TextField
              label={t("paymentAtCredentials.password")}
              // The API never returns the password, so when one is already
              // stored the field starts empty with a masked placeholder rather
              // than pretending to show the current value.
              placeholder={
                configuration.AT?.username
                  ? t("paymentAtCredentials.passwordPlaceholderStored")
                  : t("paymentAtCredentials.password")
              }
              type="password"
              value={password}
              onChange={event => setPassword(event.target.value)}
              disabled={!unlocked}
              error={passwordTooShort}
              helperText={
                passwordTooShort ? t("paymentAtCredentials.tooShort") : ""
              }
              fullWidth
              sx={outlinedFieldSx}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Box display="flex" gap={1} p={1}>
            <ActionButton
              label={t("common.cancel")}
              onClick={onClose}
              variant="secondary"
            />
            {unlocked ? (
              <ActionButton
                label={
                  configuration.AT?.username
                    ? t("paymentConfigurationDetail.updateDetails")
                    : t("paymentConfigurationDetail.registerDetails")
                }
                onClick={onSubmit}
                loading={loading}
                disabled={!isValid || loading}
              />
            ) : (
              <ActionButton
                label={t("paymentAtCredentials.unlock")}
                onClick={() => setConfirmingUnlock(true)}
                variant="secondary"
              />
            )}
          </Box>
        </DialogActions>
      </DialogComponent>

      <DialogComponent
        open={confirmingUnlock}
        handleModal={setConfirmingUnlock}
        headerTxt={t("paymentAtCredentials.unlockTitle")}
      >
        <DialogContent>
          {/* The warning has fiscal implications, keep the wording as authored. */}
          <Box sx={{ whiteSpace: "pre-line" }}>
            {t("paymentAtCredentials.unlockMessage")}
          </Box>
        </DialogContent>
        <DialogActions>
          <Box display="flex" gap={1} p={1}>
            <ActionButton
              label={t("common.no")}
              onClick={() => setConfirmingUnlock(false)}
              variant="secondary"
            />
            <ActionButton
              label={t("common.yes")}
              onClick={() => {
                setUnlocked(true);
                setConfirmingUnlock(false);
              }}
            />
          </Box>
        </DialogActions>
      </DialogComponent>
    </>
  );
}
